import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subscription } from 'rxjs';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { CaseFee, ChartsService } from 'src/app/services/charts/charts.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { CurrencyPipe, KeyValuePipe } from '@angular/common';

type CaseFeeRow = {
  partner: string;
  product_name: string | null;
  count: number;
  start_fee_sums: Record<string, number>;
  reject_fee_sums: Record<string, number>;
  final_fee_sums: Record<string, number>;
};

@Component({
  selector: 'app-case-fees-table',
  templateUrl: './case-fees-table.component.html',
  styleUrls: ['./case-fees-table.component.scss'],
  imports: [MatProgressSpinner, MatIcon, CurrencyPipe, KeyValuePipe],
})
export class CaseFeesTableComponent {
  private chartsService = inject(ChartsService);
  private snackbar = inject(MatSnackBar);

  private refreshSub?: Subscription;

  readonly expandedRows = new Set<string>();

  fees: Record<string, CaseFeeRow[]> = {};
  feeSums: Record<string, CaseFeeRow> = {};

  @Input() from_date?: Date;
  @Input() to_date?: Date;

  @Input({ required: true }) set refresh(refresh: Observable<void>) {
    this.refreshSub?.unsubscribe();

    this.refreshSub = refresh.subscribe(() => this.setData());
  }

  readonly loading = signal(false);
  @Output() loadingChange = new EventEmitter<boolean>();

  async setData(): Promise<void> {
    if (this.loading() || !this.from_date || !this.to_date) {
      return;
    }

    try {
      this.setLoading(true);

      const fees = await this.chartsService.getCaseFees({
        started_at_from: format(this.from_date, 'yyyy-MM-dd'),
        started_at_to: format(this.to_date, 'yyyy-MM-dd'),
      });

      this.fees = {};
      this.feeSums = {};

      for (const fee of fees) {
        this.addFeeToFees(fee);
        this.addFeeToSums(fee);
      }
    } catch (error: AxiosError | unknown) {
      console.error('Error while getting partner table', error);
      let message = 'Hiba a partner táblázat betöltésekor';

      if (error instanceof AxiosError && error.response?.data?.message) {
        message = error.response.data.message;
      }

      this.snackbar.open(message, 'OK', { duration: 5000 });
    } finally {
      this.setLoading(false);
    }
  }

  isPartnerExpanded(row: CaseFeeRow): boolean {
    return this.expandedRows.has(row.partner);
  }

  togglePartner(row: CaseFeeRow): void {
    if (this.expandedRows.has(row.partner)) {
      this.expandedRows.delete(row.partner);
    } else {
      this.expandedRows.add(row.partner);
    }
  }

  private addFeeToFees(fee: CaseFee): void {
    const partner = fee.partner_name ?? 'Payee';
    if (!(partner in this.fees)) {
      this.fees[partner] = [];
    }

    let product = this.fees[partner].find(
      (p) => p.product_name === fee.product_name,
    );
    if (!product) {
      product = {
        count: 0,
        partner,
        product_name: fee.product_name,
        start_fee_sums: {},
        reject_fee_sums: {},
        final_fee_sums: {},
      };
      this.fees[partner].push(product);
    }

    product.count += fee.count;

    if (fee.fee_currency in product.reject_fee_sums) {
      product.reject_fee_sums[fee.fee_currency] += fee.reject_fee_amount;
    } else {
      product.reject_fee_sums[fee.fee_currency] = fee.reject_fee_amount;
    }

    if (fee.fee_currency in product.start_fee_sums) {
      product.start_fee_sums[fee.fee_currency] += fee.start_fee_amount;
    } else {
      product.start_fee_sums[fee.fee_currency] = fee.start_fee_amount;
    }

    product.final_fee_sums[fee.fee_currency] =
      product.start_fee_sums[fee.fee_currency] -
      product.reject_fee_sums[fee.fee_currency];
  }

  private addFeeToSums(fee: CaseFee): void {
    const partner = fee.partner_name ?? 'Payee';
    if (!(partner in this.feeSums)) {
      this.feeSums[partner] = {
        count: 0,
        partner,
        product_name: null,
        start_fee_sums: {},
        reject_fee_sums: {},
        final_fee_sums: {},
      };
    }

    const sums = this.feeSums[partner];

    sums.count += fee.count;

    if (fee.fee_currency in sums.reject_fee_sums) {
      sums.reject_fee_sums[fee.fee_currency] += fee.reject_fee_amount;
    } else {
      sums.reject_fee_sums[fee.fee_currency] = fee.reject_fee_amount;
    }

    if (fee.fee_currency in sums.start_fee_sums) {
      sums.start_fee_sums[fee.fee_currency] += fee.start_fee_amount;
    } else {
      sums.start_fee_sums[fee.fee_currency] = fee.start_fee_amount;
    }

    sums.final_fee_sums[fee.fee_currency] =
      sums.start_fee_sums[fee.fee_currency] -
      sums.reject_fee_sums[fee.fee_currency];
  }

  private setLoading(loading: boolean): void {
    this.loading.set(loading);
    this.loadingChange.emit(loading);
  }
}
